<script setup lang="ts" generic="T extends { id: number; label: string }">
import { computed, type Ref, ref } from 'vue'

import { Selector } from '../../'
import type { FilterCriteriaAsyncFilter, FilterCriteriaAsyncResult } from '../../../types'
import { debounce } from '../../../utils'

const props = defineProps<{
  loadOptions: (filterRef: Ref<FilterCriteriaAsyncFilter>) => FilterCriteriaAsyncResult<T>
  placeholder: string
  size?: 'default' | 'large'
  isDisabled?: boolean
}>()

const model = defineModel<T | null>({ default: null })

const filter = ref<FilterCriteriaAsyncFilter>({ query: '' })
const { data, isLoading } = props.loadOptions(filter)

/*
 * A stored criterion only carries the label it was saved with, and the API's entity references are
 * server-populated and ignored on write — so a label the loader composes (a revenue tier prefixed
 * with its client) cannot survive a save. Preferring the freshly loaded option restores it, and
 * picks up renames while it is at it. The stored label stands in until the options arrive, and for
 * a selection that falls outside the page they came back on.
 */
const selectedOption = computed(() => data.value.find((option) => option.id === model.value?.id) ?? model.value)

const selectedLabel = computed(() => selectedOption.value?.label ?? props.placeholder)

const handleSearchQuery = debounce((value: string) => (filter.value.query = value), 500)

const handleSelection = (item: { id: number; label?: string } | null) => {
  if (!item) {
    model.value = null
    return
  }
  model.value = data.value.find((option) => option.id === item.id) ?? null
}

const handleVisible = (visible: boolean) => {
  if (visible) {
    filter.value.query = ''
  }
}
</script>

<template>
  <Selector
    :size="size"
    :is-disabled="isDisabled"
    :options="data"
    :selected-option="selectedOption"
    :selected-label="selectedLabel"
    :loading="isLoading"
    :query="filter.query"
    should-truncate-text
    :truncate-option-text="false"
    @search="handleSearchQuery"
    @update:selected-option="handleSelection"
    @visible-change="handleVisible"
  />
</template>
